import { GridLegacy, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'store';
import { useGetStockLocationsQuery } from 'store/slices/apiV1/inventory';
import { IconEdit, IconPrinter } from '@tabler/icons-react';
import { DatagridNames, RowAction } from 'types/datagrid';
import makeColumns from 'views/inventory/StockLocations/columns';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import CreateUpdateStockLocationDialog from 'views/inventory/components/CreateUpdateStockLocationDialog';
import usePrinting from 'hooks/usePrinting';
import { StockLocation, StockLocationType } from 'types/inventory';
import { ALL_APP_IDS, ALL_APPS } from 'constants/appConstants';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { appPermissionAccessLevels } from 'types/apps';
import { actionButtonSpacing, gridSpacing } from 'constants/themeConstants';
import { getTrackingStrings } from 'constants/trackingStrings';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import useGetCustomPropertyColumns from 'hooks/useGetCustomPropertyColumns';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import { StockLocationReportData } from 'types/generateReports';

const InventoryLocations = () => {
  const { activeOrgId } = useSelector((state) => state.org);
  const [newStockLocationDialogOpen, setNewStockLocationDialogOpen] =
    useState(false);
  const [selectedStockLocation, setSelectedStockLocation] =
    useState<StockLocation | null>(null);
  const { hasAppPermission } = useAppAccessContext();
  const {
    handlePrintLabel: printLabel,
    isLoadingConfig,
    isLoadingPrint,
  } = usePrinting({});

  const { openNewLocationDialog } = getTrackingStrings();
  const propertiesAppEnabled = hasAppPermission(ALL_APP_IDS.PROPERTIES);
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const handlePrintLabel = async (stockLocation: StockLocation) => {
    printLabel({
      recordType: 'stockLocation',
      recordIdProps: {
        stockLocationId: stockLocation.id,
      },
    });
  };

  const handlePrintVerticalLabel = async (stockLocation: StockLocation) => {
    printLabel({
      recordType: 'stockLocation',
      recordIdProps: {
        stockLocationId: stockLocation.id,
      },
      labelTemplateName: 'Stock Location Vertical',
    });
  };

  const { data: allStockLocations = [], isFetching } =
    useGetStockLocationsQuery({
      orgId: activeOrgId as string,
    });

  const stockLocations = useMemo(
    () =>
      allStockLocations.filter(
        (stockLocation) =>
          stockLocation.locationType !== StockLocationType.FACILITY &&
          stockLocation?.facility?.org === activeOrgId
      ),
    [allStockLocations]
  );

  const { customPropertyColumns } = useGetCustomPropertyColumns({
    model: CustomPropertyModel.STOCK_LOCATION,
    visibilityContext: CustomPropertyVisibility.DATAGRID_READ_ONLY,
  });

  const memoizedColumns = useMemo(() => {
    const columns = makeColumns({
      stockLocations,
      handleReplaceWithCustomTeamName,
    });
    return [
      ...columns,
      ...(hasAppPermission(ALL_APPS.WAREHOUSE.id)
        ? [
            {
              field: 'labelPrint',
              headerName: 'Label',
              description: 'Print stock location label',
              type: 'string',
              minWidth: 80,
              flex: 1,
              renderCell: (params: GridRenderCellParams) => (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={actionButtonSpacing}
                >
                  <SegmentButton
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      handlePrintLabel(params.row);
                    }}
                    startIcon={<IconPrinter />}
                    permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
                  >
                    Print
                  </SegmentButton>
                  <SegmentButton
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      handlePrintVerticalLabel(params.row);
                    }}
                    startIcon={<IconPrinter />}
                    permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
                  >
                    Print Vertical
                  </SegmentButton>
                </Stack>
              ),
            },
          ]
        : []),
      ...customPropertyColumns,
    ];
  }, [stockLocations]);

  return (
    <>
      <GridLegacy container rowSpacing={gridSpacing}>
        <GridLegacy item xs={12}>
          <ExtendedDatagrid<StockLocation, StockLocationReportData>
            enableStateQueryParams
            loading={isFetching || isLoadingConfig || isLoadingPrint}
            exportFileNamePrefix="InventoryLocations"
            noRows="No stock locations found."
            gridName={DatagridNames.inventoryLocations}
            rows={stockLocations}
            newRecordButton={{
              label: 'New Stock Location',
              onClick: () => {
                setNewStockLocationDialogOpen(true);
                setSelectedStockLocation(null);
              },
              permissionScope: {
                app: ALL_APPS.KITTING.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              trackingName: openNewLocationDialog,
            }}
            rowActions={
              [
                {
                  onRowClick: (row) => {
                    if (row) {
                      setSelectedStockLocation(row);
                      setNewStockLocationDialogOpen(true);
                    }
                  },
                  Icon: IconEdit,
                  getLabel: () => 'Edit',
                  isRowDisabled: (row) => !row?.customerLocation,
                  permissionScope: {
                    app: ALL_APPS.KITTING.id,
                    accessLevel: appPermissionAccessLevels.edit,
                  },
                },
              ] as RowAction<StockLocation>[]
            }
            generateReportData={{ stockLocations }}
            columns={memoizedColumns}
            checkboxSelection={false}
            initialSorting={{
              sortModel: [
                { field: 'type', sort: 'desc' },
                { field: 'location', sort: 'desc' },
                { field: 'name', sort: 'asc' },
              ],
            }}
            initialVisibleColumns={[
              'locationType',
              'name',
              'facility',
              ...(propertiesAppEnabled ? ['team'] : []),
              'labelPrint',
              'actions',
            ]}
            noRowHeightAuto
          />
        </GridLegacy>
      </GridLegacy>
      <CreateUpdateStockLocationDialog
        dialogOpen={newStockLocationDialogOpen}
        orgId={activeOrgId || ''}
        onClose={() => setNewStockLocationDialogOpen(false)}
        selectedLocation={selectedStockLocation}
      />
    </>
  );
};

export default InventoryLocations;
